import { createStore } from 'devextreme-aspnet-data-nojquery';
import utilsService from './utils.service';
import sessionService from '../session.service';

class HttpService {
    _baseURL = `/api`;

    getDxDataSource(url, key = 'id') {
        return createStore({
            key,
            loadUrl: `${this._baseURL}/${url}`.replace(/\/\//g, '/'),
            onBeforeSend: (_method, ajaxOptions) => {
                ajaxOptions.method = 'POST';
                ajaxOptions.xhrFields = { withCredentials: true };
                ajaxOptions.headers = {
                    authorization: `${sessionService.getToken()}`
                };
            }
        });
    }

    get(url, data, options = {}) {
        url = utilsService.addParamsToUrl(url, data);
        return this._request(url, { method: 'GET', headers: this._getHeaders() }, options);
    }

    delete(url, data, options = {}) {
        url = utilsService.addParamsToUrl(url, data);
        return this._request(url, { method: 'DELETE', headers: this._getHeaders() }, options);
    }

    put(url, data, options = {}) {
        return this._request(
            url,
            {
                method: 'PUT',
                headers: this._getHeaders(),
                body: data !== null && typeof data === 'object' ? JSON.stringify(data) : (data ?? undefined)
            },
            options
        );
    }

    post(url, data, options = {}) {
        return this._request(
            url,
            {
                method: 'POST',
                headers: this._getHeaders(),
                body: data !== null && typeof data === 'object' ? JSON.stringify(data) : (data ?? undefined)
            },
            options
        );
    }

    postFile(url, file, options = {}) {
        const formData = new FormData();
        formData.append('file', file);
        const headers = this._getHeaders(null); // Let browser set Content-Type with boundary for multipart
        return this._request(url, { method: 'POST', headers, body: formData }, options);
    }

    downloadReport(route, reportData, method = 'GET') {
        const token = sessionService.getToken();
        const url = `${this._baseURL}/${route}`.replace(/\/\//g, '/');

        const fetchOptions = {
            method,
            headers: { authorization: token }
        };

        if (method !== 'GET' && reportData) {
            fetchOptions.headers['Content-Type'] = 'application/json';
            fetchOptions.body = JSON.stringify(reportData);
        }

        return fetch(url, fetchOptions)
            .then(res => {
                if (!res.ok) throw new Error(`HTTP error ${res.status}`);
                const contentType = res.headers.get('content-type') || '';
                const filename = res.headers.get('filename') || '';
                return res.blob().then(blob => ({ blob, contentType, filename }));
            })
            .then(({ blob, contentType, filename }) => {
                const blobUrl = window.URL.createObjectURL(new Blob([blob], { type: contentType }));
                const link = document.createElement('a');
                link.href = blobUrl;
                link.download = filename;
                document.body.append(link);
                link.click();
                link.remove();
                setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000);
            });
    }

    _getHeaders(contentType = 'application/json') {
        const token = sessionService.getToken();
        const headers = {
            Application: 'WebPortal'
        };

        if (contentType) headers['Content-Type'] = contentType;

        if (token) headers['authorization'] = token;

        return headers;
    }

    _request(url, fetchOptions, options = {}) {
        const fullUrl = `${this._baseURL}/${url}`.replace(/\/\//g, '/');
        // Same-origin cookies (Google OAuth sign-in) must ride along for /auth/verify.
        return fetch(fullUrl, {
            ...fetchOptions,
            credentials: 'same-origin',
            signal: options.cancelToken || null
        })
            .then(response => this._responseHandler(response, options))
            .catch(err => this._errorHandler(err, options));
    }

    _responseHandler(response, options) {
        const status = response.status;

        const parseResponse =
            options.responseType === 'blob'
                ? response.blob()
                : response.text().then(text => {
                      try {
                          return JSON.parse(text);
                      } catch {
                          return text || null;
                      }
                  });

        return parseResponse.then(data => {
            if (status >= 200 && status < 300) return data;

            return Promise.reject({ response: { status, data } });
        });
    }

    _errorHandler(err, options) {
        if (err?.name === 'AbortError') throw err;

        const response = err.response || err || {};

        if (options.customCatch) return Promise.reject(response);

        const payload = response.data;
        const message =
            typeof payload === 'string'
                ? payload
                : payload && typeof payload === 'object'
                  ? payload.message
                  : undefined;

        if (response.status === 400) utilsService.showToast(message || `Oops! That Didn't Work: 400 error code`, 'warn', 8000);
        else if (response.status === 401) {
            sessionService.removeToken();
            localStorage.clear();
            window.location.href = '/auth/login';
        } else if (response.status === 404)
            utilsService.showToast(message || `Oops! That Didn't Work: 404 error code`, 'warn', 8000);
        else if (response.status === 403 || response.status >= 500)
            utilsService.showToast(message || `Oops! That Didn't Work: ${response.status} error code`, 'error', 8000);

        // Keep data as an object so callers can read err.data.message
        const data =
            typeof payload === 'object' && payload !== null
                ? payload
                : message
                  ? { message }
                  : { message: undefined };

        return Promise.reject({
            ...response,
            data,
            message
        });
    }
}

export default new HttpService();
